import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import NotFoundPage from './NotFoundPage';
import AdComponent from './AdComponent';
import '../css/RedirectPage.css';

function RedirectPage() {
  const { shortUrl } = useParams();
  const AUTO_REDIRECT_SECONDS = 0;
  const PREBOOT_DELAY_MS = 0;
  const BOOT_DELAY_MS = 0;
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [longUrl, setLongUrl] = useState('');
  const [copyState, setCopyState] = useState('Copy link');
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECONDS);
  const [prebooting, setPrebooting] = useState(false);
  const [booting, setBooting] = useState(false);
  const [referrerLabel, setReferrerLabel] = useState('a direct link');
  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://urlshortener-serverside.onrender.com/api';
  const copyTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const redirectedRef = useRef(false);
  const bootRef = useRef(null);
  const prebootRef = useRef(null);

  useEffect(() => {
    const ref = document.referrer || '';
    const normalizeSource = (value) => {
      if (!value) return 'a direct link';
      let host = value;
      try {
        host = new URL(value).hostname;
      } catch (error) {
        host = value;
      }
      host = host.replace(/^www\./, '').toLowerCase();
      if (host.includes('facebook.com') || host.includes('fb.com') || host.includes('l.facebook.com')) return 'Facebook';
      if (host.includes('instagram.com')) return 'Instagram';
      if (host.includes('tiktok.com')) return 'TikTok';
      if (host.includes('twitter.com') || host.includes('t.co') || host.includes('x.com')) return 'X (Twitter)';
      if (host.includes('linkedin.com') || host.includes('lnkd.in')) return 'LinkedIn';
      if (host.includes('reddit.com')) return 'Reddit';
      if (host.includes('youtube.com') || host.includes('youtu.be')) return 'YouTube';
      if (host.includes('news.google.com')) return 'Google News';
      if (host.includes('google.')) return 'Google';
      if (host.includes('bing.com')) return 'Bing';
      if (host.includes('duckduckgo.com')) return 'DuckDuckGo';
      if (host.includes('yahoo.com')) return 'Yahoo';
      return host;
    };
    setReferrerLabel(normalizeSource(ref));

    if (PREBOOT_DELAY_MS > 0) {
      prebootRef.current = setTimeout(() => setPrebooting(false), PREBOOT_DELAY_MS);
    } else {
      setPrebooting(false);
    }
    if (BOOT_DELAY_MS > 0) {
      bootRef.current = setTimeout(() => setBooting(false), PREBOOT_DELAY_MS + BOOT_DELAY_MS);
    } else {
      setBooting(false);
    }
    return () => {
      if (bootRef.current) clearTimeout(bootRef.current);
      if (prebootRef.current) clearTimeout(prebootRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchLongUrl = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/links/${shortUrl}`,
          { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
        );
        setLongUrl(response.data.longUrl);
      } catch (err) {
        console.error('Error fetching long URL:', err);
        setError('Failed to retrieve the original URL. The short link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchLongUrl();
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [shortUrl]);

  const shortDisplay = `${window.location.origin}/${shortUrl}`;
  const title = error ? 'We could not redirect you' : 'Review before you continue';
  const subtitle = error
    ? error
    : loading
      ? 'Preparing your destination'
      : 'This preview helps keep traffic safe and transparent.';

  const destination = useMemo(() => {
    if (!longUrl) return null;
    try {
      const parsed = new URL(longUrl);
      return {
        href: parsed.href,
        host: parsed.host,
        protocol: parsed.protocol.replace(':', ''),
        pathname: parsed.pathname,
        secure: parsed.protocol === 'https:',
      };
    } catch (err) {
      return {
        href: longUrl,
        host: longUrl,
        protocol: 'unknown',
        pathname: '',
        secure: false,
      };
    }
  }, [longUrl]);

  const handleContinue = () => {
    if (!longUrl) return;
    redirectedRef.current = true;
    if (countdownRef.current) clearInterval(countdownRef.current);
    window.location.href = longUrl;
  };

  const handleOpenNewTab = () => {
    if (!longUrl) return;
    window.open(longUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    if (!longUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(longUrl);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = longUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setCopyState('Copied');
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopyState('Copy link'), 1800);
    } catch (err) {
      setCopyState('Failed');
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopyState('Copy link'), 1800);
    }
  };

  useEffect(() => {
    if (loading || error || !longUrl || booting) return;
    if (AUTO_REDIRECT_SECONDS <= 0) {
      redirectedRef.current = true;
      if (countdownRef.current) clearInterval(countdownRef.current);
      window.location.replace(longUrl);
      return;
    }
    redirectedRef.current = false;
    setCountdown(AUTO_REDIRECT_SECONDS);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        const next = Number((prev - 0.5).toFixed(1));
        if (next <= 0) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          countdownRef.current = null;
          if (!redirectedRef.current) {
            redirectedRef.current = true;
            window.location.href = longUrl;
          }
          return 0;
        }
        return next;
      });
    }, 500);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [loading, error, longUrl, AUTO_REDIRECT_SECONDS, booting]);


  if (error) {
    return <NotFoundPage attemptedPath={`/${shortUrl}`} />;
  }

  return (
    <div className="redirect-page">
      {prebooting && (
        <div className="redirect-preboot">
          <div className="boot-card">
            <div className="boot-msg">Connection delay detected.</div>
            <div className="boot-sub">Redirecting from {referrerLabel}…</div>
            <div className="boot-dot" aria-hidden="true">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      )}
      {!prebooting && booting && (
        <div className="redirect-boot">
          <div className="boot-card">
            <div className="boot-brand">BlinkURL</div>
            <div className="boot-msg">Preparing your secure preview…</div>
            <div className="boot-dot" aria-hidden="true">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      )}
      <div className="redirect-illustration">
        <div className="brand">BlinkURL</div>
        <div className="browser-mock">
          <div className="browser-bar">
            <span className="dot red"></span>
            <span className="dot yellow"></span>
            <span className="dot green"></span>
          </div>
          <div className="browser-body">
            <div className="address-bar">
              <span>{shortDisplay}</span>
              <span className="check">✓</span>
            </div>
            <button className="go-button" type="button" onClick={handleContinue} disabled={!longUrl}>
              Continue
            </button>
          </div>
        </div>
        <svg className="paper-plane" viewBox="0 0 120 80" aria-hidden="true">
          <path d="M6 43L112 6 76 74 60 46 6 43Z" fill="#ffffff" stroke="#9db5d6" strokeWidth="3" strokeLinejoin="round" />
          <path d="M60 46L84 28" stroke="#9db5d6" strokeWidth="3" strokeLinecap="round" />
        </svg>
        <div className="floating-tag">GO</div>
      </div>

      <div className="redirect-text">
        <h2>{title}</h2>
        <p>{subtitle}</p>
        {!error && !loading && (
          <p className="countdown-text">Auto-redirecting in {countdown}s</p>
        )}
        {!error && loading && <div className="spinner"></div>}
      </div>

      {!loading && destination && (
        <>
          <section className="ad-section">
            <p className="ad-label">Sponsored</p>
            <AdComponent containerId={`taboola-mid-article-${shortUrl}`} />
          </section>
          <section className="destination-card">
            <div className="destination-header">
              <div>
                <p className="destination-label">Destination Preview</p>
                <h3>{destination.host}</h3>
              </div>
              <span className={`security-pill ${destination.secure ? 'secure' : 'insecure'}`}>
                {destination.secure ? 'Secure (HTTPS)' : 'Not secure'}
              </span>
            </div>
            <p className="destination-url">{destination.href}</p>
            <div className="action-row">
              <button className="primary" type="button" onClick={handleContinue}>
                Continue to destination
              </button>
              <button className="ghost" type="button" onClick={handleOpenNewTab}>
                Open in new tab
              </button>
              <button className="ghost" type="button" onClick={handleCopy}>
                {copyState}
              </button>
            </div>
            <p className="helper-text">
              Why am I seeing this? BlinkURL provides a short preview so you can verify the destination before
              continuing.
            </p>
            <div className="policy-row">
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <a href="/support">Support</a>
            </div>
          </section>
        </>
      )}

    </div>
  );
}

export default RedirectPage;
